import React from 'react'
import { FileText, Upload } from 'lucide-react'
import importService from '../../services/importService'
import toast from 'react-hot-toast'

/**
 * Import Button Component
 * 
 * Handles file import functionality for .txt and .md files.
 * Opens files in new tabs without automatically saving them.
 */
const ImportButton = ({ onNotesImported, className = '' }) => {
  const handleImport = async () => {
    try {
      // Open file dialog
      const files = await importService.openFileDialog()
      
      if (files.length === 0) {
        return // User cancelled
      }
      
      // Show loading state
      const progressToast = toast.loading(`Opening ${files.length} file(s)...`, { duration: Infinity })
      
      try {
        // Process files
        const { notes, errors } = await importService.processFiles(files)
        
        // Dismiss loading toast
        toast.dismiss(progressToast)
        
        if (notes.length === 0) {
          if (errors.length > 0) {
            // Show error details
            const errorMessage = errors
              .map(e => `${e.filename}: ${e.error}`)
              .join('\n')
            toast.error(`Failed to open files:\n${errorMessage}`)
          } else {
            toast.error('No valid files could be processed')
          }
          return
        }
        
        // Call callback with processed notes
        onNotesImported(notes)
        
        // Show success message
        let successMessage = `Opened ${notes.length} file(s) in new tabs`
        if (errors.length > 0) {
          successMessage += ` (${errors.length} file(s) failed)`
        }
        toast.success(successMessage)
        
        // Show error details if any
        if (errors.length > 0) {
          const errorMessage = errors
            .map(e => `${e.filename}: ${e.error}`)
            .join('\n')
          toast.error(`Some files failed to open:\n${errorMessage}`, { duration: 8000 })
        }
        
      } catch (error) {
        toast.dismiss(progressToast)
        console.error('Import processing failed:', error)
        toast.error(`Failed to process files: ${error.message}`)
      }
      
    } catch (error) {
      console.error('Import failed:', error)
      toast.error(`Failed to open files: ${error.message}`)
    }
  }

  return (
    <button
      onClick={handleImport}
      className={`flex items-center gap-2 px-3 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors ${className}`}
      title="Open text or markdown files in new tabs"
    >
      <Upload className="w-4 h-4" />
      <FileText className="w-4 h-4" />
      Open Files
    </button>
  )
}

export default ImportButton
